const WORD_PATTERN = /[\p{L}\p{M}\p{N}]+(?:['’\u200c-][\p{L}\p{M}\p{N}]+)*/gu;
const EXCLUDED_WORD_TARGET = [
    "a",
    "button",
    "input",
    "textarea",
    "select",
    "option",
    "[contenteditable='true']",
    "[data-speech-ignore]",
    ".dino-assistant-layer",
    ".dino-word-reader-toolbar"
].join(",");

interface SpeechWordSelection {
    word: string;
    range: Range;
}

function findCaretAtPoint(
    documentRef: Document,
    x: number,
    y: number
): { node: Node; offset: number } | null {
    const caretDocument = documentRef as Document & {
        caretRangeFromPoint?(x: number, y: number): Range | null;
    };
    const range = caretDocument.caretRangeFromPoint?.(x, y);

    if (range) {
        return {
            node: range.startContainer,
            offset: range.startOffset
        };
    }

    const position = documentRef.caretPositionFromPoint?.(x, y);
    return position
        ? {
            node: position.offsetNode,
            offset: position.offset
        }
        : null;
}


function expandSpeechRangeToWordBoundaries(
    sourceRange: Range
): Range {
    const range =
        sourceRange.cloneRange();

    if (
        range.startContainer.nodeType === 3
        && range.startOffset > 0
    ) {
        const startNode =
            range.startContainer as Text;

        for (const match of startNode.data.matchAll(WORD_PATTERN)) {
            const start = match.index;
            const end = start + match[0].length;

            if (
                range.startOffset > start
                && range.startOffset < end
            ) {
                range.setStart(
                    startNode,
                    start
                );
                break;
            }
        }
    }

    if (
        range.endContainer.nodeType === 3
        && range.endOffset > 0
    ) {
        const endNode =
            range.endContainer as Text;

        for (const match of endNode.data.matchAll(WORD_PATTERN)) {
            const start = match.index;
            const end = start + match[0].length;

            if (
                range.endOffset > start
                && range.endOffset < end
            ) {
                range.setEnd(
                    endNode,
                    end
                );
                break;
            }
        }
    }

    return range;
}

function pickSpeechTextSelection(
    documentRef: Document
): SpeechWordSelection | null {
    const selection = documentRef.getSelection();
    if (!selection || selection.rangeCount !== 1 || selection.isCollapsed) {
        return null;
    }

    const range = expandSpeechRangeToWordBoundaries(
        selection.getRangeAt(0)
    );
    const ancestor = range.commonAncestorContainer.nodeType === 3
        ? range.commonAncestorContainer.parentElement
        : range.commonAncestorContainer as Element;
    if (
        !ancestor
        || !ancestor.closest("main")
        || ancestor.closest(EXCLUDED_WORD_TARGET)
    ) {
        return null;
    }

    const text = range.toString().replace(/\s+/gu, " ").trim();
    if (!text || text.length > 500) return null;

    return {
        word: text,
        range: range.cloneRange()
    };
}

function pickSpeechWordAtPoint(
    documentRef: Document,
    x: number,
    y: number
): SpeechWordSelection | null {
    const caret = findCaretAtPoint(documentRef, x, y);
    if (!caret || caret.node.nodeType !== 3) return null;

    const textNode = caret.node as Text;
    const parent = textNode.parentElement;
    if (
        !parent
        || !parent.closest("main")
        || parent.closest(EXCLUDED_WORD_TARGET)
    ) {
        return null;
    }

    for (const match of textNode.data.matchAll(WORD_PATTERN)) {
        const start = match.index;
        const end = start + match[0].length;
        if (caret.offset < start || caret.offset > end) continue;

        const range = documentRef.createRange();
        range.setStart(textNode, start);
        range.setEnd(textNode, end);

        return {
            word: match[0],
            range
        };
    }

    return null;
}

export {
    expandSpeechRangeToWordBoundaries,
    pickSpeechTextSelection,
    pickSpeechWordAtPoint
};
export type { SpeechWordSelection };
